import { Box, Button, IconButton, Stack, Switch, Typography } from '@mui/material';
import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PredictionMap, { PredictionMapHandle } from '../components/PredictionMap';
import useAddressInput from '../../../hooks/useAddressInput';
import useForecastFrames from '../hooks/useForecastFrames';
import { useCheckImgeIdExistence } from '../hooks/useCheckImgeIdExistence';
import useGetAlert from '../../../features/alerts/hooks/useGetAlertsByClient';
import useGetLocation from '../../../hooks/useGetLocation';
import AlertsFromJMA from '../../../features/AlertsFromJMA';
import { calculatedTimeType, latlngType } from '../types';
import PredicionMapContext from '../preditionMapContext';
import {
  HOUR_MS,
  SliderSetup,
  computeSliderSetup,
  defaultSliderMarks,
  findSliderMaxMin,
} from '../functions/forecastFrames';
import { useStations } from '../hooks/useStations';
import { getRivfldDepth, RivfldDepth, Station } from '../api/getStations';
import StationDetailModal from '../components/StationDetailModal';
import { format, parseISO } from 'date-fns';
import useFetchUser from '../../../features/client/hooks/useFetchUser';
import { authType } from '../../../features/client/types';
import { useLoaderData } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '../../../hooks/useIsMobile';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import LocationOnIcon from '@mui/icons-material/LocationOnOutlined';
import { sendAnalyticsEvent } from '../../../utils/googleAnalytics';

const PredictionMapPage: React.FC = () => {
  // const [calculatedTime, setCalculatedTime] = useState<Date | undefined>();
  const { t } = useTranslation();
  // X時間先の予報
  const [increment, setIncrement] = useState<number>(6);
  const [calculatedTime, setCalculatedTime] = useState<calculatedTimeType>(null); // 手動で起算時刻を設定した時に変化.
  const { isImageIdChecking, imageIdList } = useCheckImgeIdExistence({
    calculatedTime,
    setCalculatedTime,
  });
  const delta = calcDelta(calculatedTime, increment);
  const { latlng, AddressInput } = useAddressInput({ onSearch: true });
  const [chosenLatLon, setChosenLatlon] = useState<latlngType>(null);
  const [sliderValue, setSliderValue] = useState<number>(0);

  // 起算時刻が決まったら、スライダーの初期値（通常は現在時刻）と目盛りをここで一緒に決める.
  // 初期値が決まる前の仮の値（0）で地図の URL を取りに行かないよう、確定するまで選択時刻は null にする.
  const [sliderSetup, setSliderSetup] = useState<SliderSetup | null>(null);
  useEffect(() => {
    if (!calculatedTime) {
      return;
    }
    const setup = computeSliderSetup(calculatedTime, new Date());
    setSliderValue(setup.initialValue);
    setSliderSetup(setup);
  }, [calculatedTime]);
  const calculatedTimeMs = calculatedTime?.getTime() ?? null;
  const isSliderReady = calculatedTimeMs !== null && sliderSetup?.calculatedTimeMs === calculatedTimeMs;
  const sliderMarks = isSliderReady && sliderSetup ? sliderSetup.marks : defaultSliderMarks;
  const { sliderMax, sliderMin } = findSliderMaxMin(sliderMarks);
  const selectedValidTimeMs = isSliderReady ? calculatedTimeMs + sliderValue * HOUR_MS : null;
  const rangeMs = useMemo(
    () => (isSliderReady ? { min: calculatedTimeMs + sliderMin * HOUR_MS, max: calculatedTimeMs + sliderMax * HOUR_MS } : null),
    [isSliderReady, calculatedTimeMs, sliderMin, sliderMax],
  );
  const {
    selectedFrame,
    requestFrame,
    retrySelected,
    notifySelectedTilesSettled,
  } = useForecastFrames({ imageIdList, increment, delta, selectedValidTimeMs, rangeMs });
  const { stations } = useStations();
  const authInfo = useLoaderData() as authType;
  const { isPaid } = useFetchUser(authInfo.user.cognito_username);

  const alerts = useGetAlert();
  const currentPosition = useGetLocation();
  const predictionMapRef = useRef<PredictionMapHandle | null>(null);

  const goToCurrentLocation = useCallback(() => {
    if (!currentPosition) {
      return;
    }
    // GA4: 現在地ボタンの利用を計測
    sendAnalyticsEvent('map_interaction', {
      action: 'go_to_current_location',
    });
    const newPosition = currentPosition?.map(c => c + 1e-5) as [number, number];
    setChosenLatlon(newPosition);
  }, [currentPosition]);

  useEffect(() => {
    if (!chosenLatLon) {
      setChosenLatlon(currentPosition);
    }
    setChosenLatlon(latlng);
  }, [latlng]);

  const [ station, setStation ] = useState<Station | null>(null);
  const [ rivfldDepth, setRivfldDepth ] = useState<RivfldDepth | null>(null);
  const handleShowDetail = useCallback(async (index: number) => {
    const station: Station = stations[index];

    const rivfldDepth = await getRivfldDepth(station.id);

    // GA4: 河川水位詳細の表示を計測
    sendAnalyticsEvent('water_level_detail', {
      action: 'open',
      station_id: station.id,
      has_forecast_data: !!rivfldDepth?.analysis_results?.length,
    });

    setRivfldDepth(rivfldDepth);
    setStation(station);
  }, [stations]);

  const handleCloseDetail = () => {
    // GA4: 河川水位詳細を閉じた操作を計測
    sendAnalyticsEvent('water_level_detail', {
      action: 'close',
      station_id: station?.id,
    });
    setStation(null);
  }

  const [checked, setChecked] = useState<boolean>(isPaid);
  const liverLineChartData = useMemo(() => {
    if (!rivfldDepth) {
      return null;
    }

    const now = new Date().getTime();
    const latestIndex: string = format(new Date(), 'M月d日H時');

    const labels: string[] = [];
    const pastData: number[] = [];
    const forecastData: (null | number)[] = [];
    let nowIndex = 0;

    for (const d of (rivfldDepth?.analysis_results || [])) {
      const dateObj = parseISO(d.time);
      const time = dateObj.getTime();
      if (time < now) {
        nowIndex = nowIndex + 1;

        pastData.push(d.depth);
        forecastData.push(null);
      }

      if (now < time && checked) {
        forecastData.push(d.depth);
      }

      labels.push(format(dateObj, 'M月d日H時'));
    }

    return {
      labels,
      pastData,
      forecastData,
      latestIndex,
      nowIndex
    }

  }, [rivfldDepth, checked]);


  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    // GA4: 水位詳細内の予測表示切替を計測
    sendAnalyticsEvent('water_level_detail', {
      action: 'toggle_forecast',
      enabled: event.target.checked,
      station_id: station?.id,
    });
    setChecked(event.target.checked);
  };

  const [isFloodDepth, setIsFloodDepth] = useState(true);
  const [isWaterLevel, setIsWaterLevel] = useState(false);
  const [isJmaAlerts, setIsJmaAlerts] = useState(false);
  const [isRegisteredInfoVisible, setIsRegisteredInfoVisible] = useState(true);
  const visibleAlerts = useMemo(() => isRegisteredInfoVisible ? alerts : [], [alerts, isRegisteredInfoVisible]);
  const visibleStations = useMemo(() => isWaterLevel ? stations : [], [isWaterLevel, stations]);
  const isMobile = useIsMobile();

  // 地図に実際に出ている時刻. 選択中の時刻の URL を待つ間は前の時刻の地図を残すので、選択中の時刻とは別に持つ.
  const [displayedFrame, setDisplayedFrame] = useState<{ validTimeMs: number; calculatedTimeMs: number } | null>(null);
  const displayedFrameRef = useRef(displayedFrame);
  displayedFrameRef.current = displayedFrame;

  const selectedKey = selectedFrame?.key ?? null;
  const selectedStatus = selectedFrame?.status ?? null;
  const selectedUrl = selectedFrame?.url ?? null;
  useEffect(() => {
    const predictionMap = predictionMapRef.current;
    if (!isFloodDepth) {
      predictionMap?.updateGeeUrl('');
      return;
    }
    if (selectedValidTimeMs === null || calculatedTimeMs === null) {
      return;
    }

    // 選択中の時刻の URL が揃ったものだけを地図へ渡す. 他の時刻の先読みはここでは待たない.
    if (selectedStatus === 'ready' && selectedKey && selectedUrl) {
      let active = true;
      setDisplayedFrame({ validTimeMs: selectedValidTimeMs, calculatedTimeMs });
      predictionMap?.updateGeeUrl(selectedUrl).then(() => {
        if (active) {
          notifySelectedTilesSettled(selectedKey);
        }
      });
      return () => {
        active = false;
      };
    }

    // その時刻のデータが無い場合は従来どおり浸水深を消す.
    if (selectedStatus === 'unavailable') {
      predictionMap?.updateGeeUrl('');
      setDisplayedFrame({ validTimeMs: selectedValidTimeMs, calculatedTimeMs });
      return;
    }

    // 取得中・失敗は前の地図を残す. ただし起算時刻を切り替えた後は、前の予報の地図を残さない.
    if (displayedFrameRef.current && displayedFrameRef.current.calculatedTimeMs !== calculatedTimeMs) {
      predictionMap?.updateGeeUrl('');
      setDisplayedFrame(null);
    }
  }, [
    isFloodDepth,
    selectedKey,
    selectedStatus,
    selectedUrl,
    selectedValidTimeMs,
    calculatedTimeMs,
    notifySelectedTilesSettled,
  ]);

  return (
    <PredicionMapContext.Provider
      value={{
        sliderValue,
        setSliderValue,
        calculatedTime,
        setCalculatedTime,
        imageIdList,
        sliderMarks,
        selectedFrame,
        displayedValidTimeMs: displayedFrame?.validTimeMs ?? null,
        requestFrame,
        retrySelected,
      }}
    >
      <Stack style={{ width: '100%', height: '100%' }} direction="row">
        <Box position="relative" style={{ width: '100%', height: '100%' }}>
          <Box position="absolute" zIndex="2" sx={{
            top: 32,
            ...(isMobile
              ? { margin: '0 auto', left: 0, right: 0 }
              : { left: 24 }
            )
            
          }}>
            <Stack direction="row" spacing={3} justifyContent={ isMobile ? 'center' : 'start'}>
              <Button sx={{ width: 120, fontWeight: 700 }} onClick={() => {
                // GA4: 浸水深レイヤーの表示切替を計測
                sendAnalyticsEvent('prediction_map_layer', { layer: 'flood_depth', enabled: !isFloodDepth });
                setIsFloodDepth(!isFloodDepth);
              }} variant={isFloodDepth ? 'contained' : 'outlined'} color="success">{t('pages.predictionMap.fld_prediction')}</Button>
              <Button sx={{ width: 120, fontWeight: 700 }} onClick={() => {
                // GA4: 河川水位レイヤーの表示切替を計測
                sendAnalyticsEvent('prediction_map_layer', { layer: 'water_level', enabled: !isWaterLevel });
                setIsWaterLevel(!isWaterLevel);
              }} variant={isWaterLevel ? 'contained' : 'outlined'} color="success">{t('pages.predictionMap.precipitation_pred')}</Button>
              <Button sx={{ width: 120, fontWeight: 700 }} onClick={() => {
                // GA4: 気象警報レイヤーの表示切替を計測
                sendAnalyticsEvent('prediction_map_layer', { layer: 'jma_alerts', enabled: !isJmaAlerts });
                setIsJmaAlerts(!isJmaAlerts);
              }} variant={isJmaAlerts ? 'contained' : 'outlined'} color="success">{t('pages.jmaAlerts.title')}</Button>
            </Stack>
          </Box>

          <Stack
            position="absolute"
            zIndex="2"
            sx={{
              top: 88,
              ...(isMobile
                ? {
                    width: 400,
                    left: 0,
                    right: 0,
                    margin: '0 auto',
                    textAlign: 'center',
                  }
                : { left: 24 }
              ),
            }}
            direction="row"
            spacing={2}
            justifyContent="center"
          >
            {AddressInput}

            <Stack sx={{ width: 40, height: 40, boxShadow: '0 2px 8px rgba(0,0,0,0.15)', backgroundColor: '#fff', borderRadius: 2 }}>
              <IconButton onClick={goToCurrentLocation}>
                <GpsFixedIcon />
              </IconButton>
            </Stack>
          </Stack>
          
          {isJmaAlerts &&
            <Box sx={{
              position: 'absolute',
              zIndex: 99,
              ...(isMobile
                ? { width: 350, top: 'calc(50% - 140px)', right: 0, left: 0, margin: '0 auto' }
                : { top: 24, right: 24 })
            }}>
              <AlertsFromJMA />
            </Box>
          }

          <PredictionMap
            ref={predictionMapRef}
            latlng={chosenLatLon}
            alerts={visibleAlerts}
            goToCurrentLocation={goToCurrentLocation}
            pins={visibleStations}
            onClick={handleShowDetail}
            setIncrement={setIncrement}
            increment={increment}
            isImageIdChecking={isImageIdChecking}
          />

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="end"
            spacing={1}
            sx={{
              width: 136,
              height: 32,
              position: 'absolute',
              zIndex: 3,
              right: 0,
              left: 0,
              margin: '0 auto',
              ...(isMobile ? { bottom: 188 } : { bottom: 24 }),
              padding: '0 10px 0 4px',
              borderRadius: '8px',
              backgroundColor: 'rgba(0, 0, 0, 0.72)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
            }}
          >
            <Switch
              checked={isRegisteredInfoVisible}
              onChange={(event) => {
                const enabled = event.target.checked;
                sendAnalyticsEvent('prediction_map_layer', { layer: 'registered_info', enabled });
                setIsRegisteredInfoVisible(enabled);
              }}
              size="small"
              inputProps={{ 'aria-label': '登録情報の表示切替' }}
              sx={{
                width: 28,
                height: 14,
                padding: 0,
                '& .MuiSwitch-switchBase': {
                  padding: '2px 1px 0',
                  transform: 'translateX(2px)',

                  '&.Mui-checked': {
                    transform: 'translateX(14px)',
                    color: '#fff',
                    '& + .MuiSwitch-track': {
                      backgroundColor: '#f02424',
                      opacity: 1,
                    },
                  },
                },
                '& .MuiSwitch-thumb': {
                  width: 10,
                  height: 10,
                  boxShadow: 'none',
                },
                '& .MuiSwitch-track': {
                  borderRadius: 11,
                  backgroundColor: '#9e9e9e',
                  opacity: 1,
                },
              }}
            />
            <Stack direction="row" spacing={0.25} alignItems="center">
              <Box
                sx={{
                  width: 8,
                  height: 7,
                  border: '1px solid',
                  borderColor: isRegisteredInfoVisible ? '#f02424' : '#fff',
                  backgroundColor: 'transparent',
                }}
              />
              <LocationOnIcon
                sx={{
                  color: isRegisteredInfoVisible ? '#f02424' : '#fff',
                  fontSize: 14,
                }}
              />
            </Stack>
            <Typography sx={{ color: '#fff', fontSize: 10, fontWeight: 700, lineHeight: 1 }}>
              {t('pages.predictionMap.registeredInfo')}
            </Typography>
          </Stack>
        </Box>
          {station && (
            <StationDetailModal
              station={station}
              rivfldDepth={rivfldDepth}
              liverLineChartData={liverLineChartData}
              checked={checked}
              isPaid={isPaid}
              onChange={handleChange}
              onClose={handleCloseDetail}
              t={t}
            />
          )}
      </Stack>
    </PredicionMapContext.Provider>
  );
};

export default PredictionMapPage;

const calcDelta = (calculatedTime: calculatedTimeType, increment: number) => {
  if (!calculatedTime) {
    const now = new Date();
    const diffInHours = now.getHours() % increment;
    return diffInHours;
  }
  const now = new Date();
  const diffInHours = Math.floor((now.getTime() - calculatedTime.getTime()) / (1000 * 60 * 60)); // 差分を時間で取得
  let delta = 0;
  if (diffInHours >= 0 && diffInHours <= 39) {
    delta = Math.floor(diffInHours % increment);
    return delta;
  }
  return delta;
};
